import type { CodigoDePendencia, MotivoNaoEmite, Pendencia, Prontidao } from "@/api/contrato";
import {
  rotaDaAplicacao,
  rotaDoCedente,
  rotaDoConvenio,
  rotaDoVinculo,
  type AbaDaAplicacao,
  type AbaDoCedente,
} from "@/rotas";

/*
 * Código de pendência em palavras de tela: até cinco palavras, e o porquê vai em
 * `Dica` (spec `pendencias-do-cedente`). O painel traduz, e não repete a regra: o que
 * bloqueia, e em que ordem, vem pronto do servidor.
 *
 * `Record` sobre o enum do contrato: código novo não compila até ganhar texto, dica e
 * ação, e `Pagina.test.tsx` confere as tabelas contra o enum gerado.
 */
export const ROTULO_DA_PENDENCIA: Record<CodigoDePendencia, string> = {
  cedente_bloqueado: "Cedente bloqueado",
  sem_conta: "Sem conta",
  sem_convenio: "Sem convênio",
  banco_sem_ficha: "Banco sem ficha",
  conta_incompleta: "Conta incompleta",
  convenio_incompleto: "Convênio incompleto",
  convenio_bloqueado: "Convênio bloqueado",
  convenio_nao_provisionado: "Não provisionado",
  certificado_vencido: "Certificado vencido",
  sem_certificado: "Sem certificado",
  certificado_vencendo: "Certificado vencendo",
  sem_aplicacao: "Sem aplicação",
  aplicacao_suspensa: "Aplicação suspensa",
  aplicacao_sem_contrato: "Aplicação sem contrato",
  vinculo_suspenso: "Vínculo suspenso",
  vinculo_sem_contrato: "Sem contrato",
  vinculo_sem_convenio: "Nenhum convênio liberado",
  sem_chave: "Sem chave de emissão",
  entrega_sem_webhook: "Sem entrega de eventos",
  sem_endereco: "Sem endereço",
  sem_contato: "Sem contato",
};

/** O porquê de cada pendência. A linha fica com o fato e a ação. */
export const DICA_DA_PENDENCIA: Record<CodigoDePendencia, string> = {
  cedente_bloqueado: "A emissão recusa boleto de cedente bloqueado.",
  sem_conta: "O boleto é emitido numa conta ativa do cedente.",
  sem_convenio: "O boleto sai por um convênio ativo da conta.",
  banco_sem_ficha: "Este banco não gera o PDF do boleto.",
  conta_incompleta: "A conta não atende às regras do banco, e ainda pode ser corrigida.",
  convenio_incompleto: "O convênio não atende às regras do banco, e ainda pode ser corrigido.",
  convenio_bloqueado: "A emissão recusa convênio bloqueado. O cedente só para se não sobrar outro ativo.",
  convenio_nao_provisionado:
    "O convênio ainda não foi levado à TecnoSpeed, e o boleto pode falhar depois de aceito.",
  certificado_vencido: "A emissão recusa boleto com certificado vencido.",
  sem_certificado: "No WebService, este banco exige certificado na TecnoSpeed.",
  certificado_vencendo: "Vence em 30 dias ou menos. Depois disso, a emissão recusa.",
  sem_aplicacao: "Quem emite é a aplicação vinculada ao cedente.",
  aplicacao_suspensa: "Aplicação suspensa não emite para nenhum cedente.",
  aplicacao_sem_contrato: "A aplicação só emite com contrato vigente.",
  vinculo_suspenso: "Vínculo suspenso não emite, mesmo com a aplicação ativa.",
  vinculo_sem_contrato: "Na cobrança por cedente, cada vínculo precisa de contrato vigente.",
  vinculo_sem_convenio: "O vínculo não libera nenhum convênio ativo deste cedente.",
  sem_chave: "A aplicação precisa de chave de acesso válida, com permissão de emissão.",
  entrega_sem_webhook: "A emissão funciona, mas a aplicação não recebe os eventos do boleto.",
  sem_endereco: "A TecnoSpeed exige endereço completo no cadastro do cedente.",
  sem_contato: "A TecnoSpeed exige telefone e e-mail no cadastro do cedente.",
};

/**
 * Bloqueante em vermelho, aviso em âmbar.
 *
 * Sai de `bloqueia`, e não do código: `sem_convenio`, `banco_sem_ficha` e
 * `convenio_bloqueado` são aviso quando sobra outro caminho, e quem sabe é o servidor.
 */
export function matizDaPendencia({ bloqueia }: Pendencia): "falha" | "transito" {
  return bloqueia ? "falha" : "transito";
}

/*
 * Identificador ausente cai na aba, sem modal: o contrato o promete para o nível da
 * pendência, e um modal sem a conta dele não teria o que mostrar.
 */
function aoConvenio(pessoaID: string, { convenio_id }: Pendencia): string {
  return convenio_id === undefined
    ? rotaDoCedente(pessoaID, "contas")
    : rotaDoConvenio(pessoaID, convenio_id);
}

function aoVinculo(pessoaID: string, { sistema_id }: Pendencia): string {
  return sistema_id === undefined
    ? rotaDoCedente(pessoaID, "aplicacoes")
    : rotaDoVinculo(pessoaID, sistema_id);
}

function aAplicacao(aba?: AbaDaAplicacao) {
  return (pessoaID: string, { sistema_id }: Pendencia): string =>
    sistema_id === undefined
      ? rotaDoCedente(pessoaID, "aplicacoes")
      : rotaDaAplicacao(sistema_id, aba);
}

/*
 * O convênio tem o painel dele, e o nível cedente tem a aba Dados. No nível do par, a pendência
 * diz que nenhum convênio liberado à aplicação está em dia: o vínculo mostra quais são, e a
 * pendência de cada convênio traz a própria ação.
 */
function aoCertificado(pessoaID: string, pendencia: Pendencia): string {
  if (pendencia.nivel === "cedente") return rotaDoCedente(pessoaID, "dados", { abrir: "certificado" });
  if (pendencia.nivel === "par") return aoVinculo(pessoaID, pendencia);
  return pendencia.convenio_id === undefined
    ? aoConvenio(pessoaID, pendencia)
    : rotaDoConvenio(pessoaID, pendencia.convenio_id, { abrir: "certificado" });
}

function abaDoCertificado({ nivel }: Pendencia): AbaDoCedente {
  if (nivel === "cedente") return "dados";
  return nivel === "par" ? "aplicacoes" : "contas";
}

function aNovaConta(pessoaID: string): string {
  return rotaDoCedente(pessoaID, "contas", { abrir: "nova-conta" });
}

function aosDados(pessoaID: string): string {
  return rotaDoCedente(pessoaID, "dados", { abrir: "editar" });
}

type RotuloDaAcao =
  | "Reativar"
  | "Nova conta"
  | "Novo convênio"
  | "Editar conta"
  | "Editar convênio"
  | "Abrir convênio"
  | "Provisionar"
  | "Trocar certificado"
  | "Enviar certificado"
  | "Vincular aplicação"
  | "Abrir aplicação"
  | "Abrir vínculo"
  | "Abrir chaves"
  | "Abrir entrega"
  | "Editar dados";

interface ItemDoCatalogo {
  /** Função quando a aba depende do nível: o certificado mora no convênio ou no cedente. */
  aba: AbaDoCedente | ((pendencia: Pendencia) => AbaDoCedente);
  rotulo: RotuloDaAcao;
  /** Abre um modal de escrita: some para quem não é `admin`. */
  escreve: boolean;
  /** `null` é ação da própria página, sem navegação. */
  para: (pessoaID: string, pendencia: Pendencia) => string | null;
}

/*
 * Ação de pendência é navegação (design D2): o caminho mais `?abrir=`, e a página não
 * conhece os modais das abas. `Reativar` é a exceção, porque não há para onde ir: o
 * interruptor do cedente mora no cabeçalho desta mesma página, e por isso a linha nem
 * aparece (`pendenciasDaLista`).
 */
const CATALOGO: Record<CodigoDePendencia, ItemDoCatalogo> = {
  cedente_bloqueado: { aba: "dados", rotulo: "Reativar", escreve: true, para: () => null },
  sem_conta: { aba: "contas", rotulo: "Nova conta", escreve: true, para: aNovaConta },
  sem_convenio: {
    aba: "contas",
    rotulo: "Novo convênio",
    escreve: true,
    para: (pessoaID, { conta_id: conta }) =>
      rotaDoCedente(
        pessoaID,
        "contas",
        conta === undefined ? undefined : { abrir: "novo-convenio", conta },
      ),
  },
  banco_sem_ficha: { aba: "contas", rotulo: "Nova conta", escreve: true, para: aNovaConta },
  conta_incompleta: {
    aba: "contas",
    rotulo: "Editar conta",
    escreve: true,
    para: (pessoaID, { conta_id: conta }) =>
      rotaDoCedente(
        pessoaID,
        "contas",
        conta === undefined ? undefined : { abrir: "editar-conta", conta },
      ),
  },
  convenio_incompleto: {
    aba: "contas",
    rotulo: "Editar convênio",
    escreve: true,
    para: (pessoaID, pendencia) =>
      pendencia.convenio_id === undefined
        ? aoConvenio(pessoaID, pendencia)
        : rotaDoConvenio(pessoaID, pendencia.convenio_id, { abrir: "editar" }),
  },
  convenio_bloqueado: { aba: "contas", rotulo: "Abrir convênio", escreve: false, para: aoConvenio },
  convenio_nao_provisionado: {
    aba: "contas",
    rotulo: "Provisionar",
    escreve: true,
    para: (pessoaID, pendencia) =>
      pendencia.convenio_id === undefined
        ? aoConvenio(pessoaID, pendencia)
        : rotaDoConvenio(pessoaID, pendencia.convenio_id, { abrir: "provisionar" }),
  },
  certificado_vencido: {
    aba: abaDoCertificado,
    rotulo: "Trocar certificado",
    escreve: true,
    para: aoCertificado,
  },
  sem_certificado: {
    aba: abaDoCertificado,
    rotulo: "Enviar certificado",
    escreve: true,
    para: aoCertificado,
  },
  certificado_vencendo: {
    aba: abaDoCertificado,
    rotulo: "Trocar certificado",
    escreve: true,
    para: aoCertificado,
  },
  sem_aplicacao: {
    aba: "aplicacoes",
    rotulo: "Vincular aplicação",
    escreve: true,
    para: (pessoaID) => rotaDoCedente(pessoaID, "aplicacoes", { abrir: "vincular" }),
  },
  aplicacao_suspensa: {
    aba: "aplicacoes",
    rotulo: "Abrir aplicação",
    escreve: false,
    para: aAplicacao(),
  },
  aplicacao_sem_contrato: {
    aba: "aplicacoes",
    rotulo: "Abrir aplicação",
    escreve: false,
    para: aAplicacao("contrato"),
  },
  vinculo_suspenso: { aba: "aplicacoes", rotulo: "Abrir vínculo", escreve: false, para: aoVinculo },
  vinculo_sem_contrato: {
    aba: "aplicacoes",
    rotulo: "Abrir vínculo",
    escreve: false,
    para: aoVinculo,
  },
  vinculo_sem_convenio: {
    aba: "aplicacoes",
    rotulo: "Abrir vínculo",
    escreve: false,
    para: aoVinculo,
  },
  sem_chave: {
    aba: "aplicacoes",
    rotulo: "Abrir chaves",
    escreve: false,
    para: aAplicacao("chaves"),
  },
  entrega_sem_webhook: {
    aba: "aplicacoes",
    rotulo: "Abrir entrega",
    escreve: false,
    para: aAplicacao("entrega"),
  },
  sem_endereco: { aba: "dados", rotulo: "Editar dados", escreve: true, para: aosDados },
  sem_contato: { aba: "dados", rotulo: "Editar dados", escreve: true, para: aosDados },
};

export interface AcaoDaPendencia {
  rotulo: string;
  escreve: boolean;
  para: string | null;
}

/**
 * As linhas da lista, na ordem do servidor.
 *
 * `cedente_bloqueado` fica de fora: o selo `bloqueado` diz o fato, e o interruptor do
 * cabeçalho é a ação. A linha seria o mesmo fato e o mesmo controle pela segunda vez.
 */
export function pendenciasDaLista(prontidao: Prontidao | undefined): readonly Pendencia[] {
  return (prontidao?.pendencias ?? []).filter(({ codigo }) => codigo !== "cedente_bloqueado");
}

/**
 * Se a lista de pendências já oferece esta ação. Com ela na tela, a aba não repete nem o
 * texto do vazio nem o botão: uma ação só por intenção visível. `contaID` restringe a
 * uma conta, porque `Novo convênio` é por conta.
 *
 * Sem prontidão, é `false`: a lista não aparece, e a aba volta a responder sozinha.
 */
export function pendenciaOferece(
  prontidao: Prontidao | undefined,
  acao: RotuloDaAcao,
  contaID?: string,
): boolean {
  return pendenciasDaLista(prontidao).some(
    ({ codigo, conta_id }) =>
      CATALOGO[codigo].rotulo === acao && (contaID === undefined || conta_id === contaID),
  );
}

/**
 * Se a página já diz por que este par não emite: na lista de pendências, com o nome da
 * aplicação, ou no selo, quando o motivo é do cedente. A linha da aba fica com `não emite`.
 */
export function motivoJaDito(
  prontidao: Prontidao | undefined,
  sistemaID: string,
  motivo: MotivoNaoEmite,
): boolean {
  return (prontidao?.pendencias ?? []).some(
    ({ codigo, sistema_id }) =>
      codigo === motivo && (sistema_id === undefined || sistema_id === sistemaID),
  );
}

/** A ação de uma linha: rótulo, se escreve, e o caminho. `para: null` é `Reativar`. */
export function acaoDaPendencia(pessoaID: string, pendencia: Pendencia): AcaoDaPendencia {
  const { rotulo, escreve, para } = CATALOGO[pendencia.codigo];
  return { rotulo, escreve, para: para(pessoaID, pendencia) };
}

function abaDaPendencia(pendencia: Pendencia): AbaDoCedente {
  const { aba } = CATALOGO[pendencia.codigo];
  return typeof aba === "function" ? aba(pendencia) : aba;
}

/**
 * A aba em que `/cedentes/:id` abre: a da primeira pendência, e `contas` sem nenhuma.
 *
 * Primeira na ordem do servidor, e não na ordem das abas: as bloqueantes vêm antes, e
 * um aviso em Dados não pode roubar a abertura do elo que parou a cadeia em Contas.
 */
export function abaInicial(prontidao: Prontidao | undefined): AbaDoCedente {
  const primeira = prontidao?.pendencias[0];
  return primeira === undefined ? "contas" : abaDaPendencia(primeira);
}

/** As bloqueantes por aba, para a marca. Aviso não conta, como no selo. */
export function marcasDasAbas(prontidao: Prontidao | undefined): Partial<Record<AbaDoCedente, number>> {
  const marcas: Partial<Record<AbaDoCedente, number>> = {};
  for (const pendencia of pendenciasDaLista(prontidao)) {
    if (!pendencia.bloqueia) continue;
    const aba = abaDaPendencia(pendencia);
    marcas[aba] = (marcas[aba] ?? 0) + 1;
  }
  return marcas;
}
